/**
 * AppUpdateNotice - tells someone a newer build is available, and shows what
 * changed.
 *
 * Two states, both driven by `useAppUpdateStore`:
 *
 *  - Optional update: a quiet button in the footer. Nothing is interrupted;
 *    the user reloads when it suits them, or simply next time they open the app.
 *
 *  - Mandatory update: a fixed bar across the bottom that cannot be dismissed,
 *    counting down to an automatic reload. It is fixed rather than sitting in
 *    the footer flow because a required update that scrolls off screen is not a
 *    required update. The grace period exists so someone mid-form can submit
 *    first, which a hard block would have cost them.
 *
 * The modal lists every release the running bundle is behind, not just the
 * newest, so a user who has not reloaded in a fortnight still sees the lot.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button, Text, Badge } from '@/components/ui/core';
import { Group, Stack, ScrollArea } from '@/components/ui/layout';
import { Modal } from '@/components/ui/overlays';
import { Divider } from '@/components/ui/data-display';
import { IconSparkles, IconRefresh, IconAlertTriangle } from '@tabler/icons-react';
import { useAppUpdateStore } from '@/domain/appUpdate';
import { clearPretendVersion, type ReleaseChangeKind } from '@/config/releases';

const KIND_LABEL: Record<ReleaseChangeKind, string> = {
  added: 'New',
  improved: 'Improved',
  fixed: 'Fixed',
};

const KIND_COLOUR: Record<ReleaseChangeKind, string> = {
  added: 'teal',
  improved: 'blue',
  fixed: 'grape',
};

/** mm:ss, floored at zero so a late tick never renders a negative countdown. */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function AppUpdateNotice() {
  const pending = useAppUpdateStore((s) => s.pending);
  const mandatory = useAppUpdateStore((s) => s.mandatory);
  const deadlineAt = useAppUpdateStore((s) => s.deadlineAt);
  const modalOpen = useAppUpdateStore((s) => s.modalOpen);
  const openModal = useAppUpdateStore((s) => s.openModal);
  const closeModal = useAppUpdateStore((s) => s.closeModal);

  const [remaining, setRemaining] = useState<number | null>(null);

  const reload = useCallback(() => {
    // In development the pretend-version override is what made this notice
    // appear, and it outlives the reload. Clearing it first stops a mandatory
    // update reloading into itself over and over. No-op in production.
    clearPretendVersion();
    window.location.reload();
  }, []);

  useEffect(() => {
    if (!mandatory || !deadlineAt) {
      setRemaining(null);
      return;
    }

    const tick = () => {
      const left = deadlineAt - Date.now();
      setRemaining(left);
      // Reload rather than re-render at zero. Everything below this point is
      // about to be replaced by the new bundle anyway.
      if (left <= 0) reload();
    };

    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [mandatory, deadlineAt, reload]);

  if (pending.length === 0) return null;

  const changeList = (
    <ScrollArea style={{ maxHeight: 420 }}>
      <Stack gap="lg">
        {pending.map((release, index) => (
          <Stack key={release.version} gap="xs">
            <Group gap="xs" wrap="wrap">
              <Text fw={600}>{release.title}</Text>
              <Badge size="sm" variant="light">
                v{release.version}
              </Badge>
              <Text size="xs" c="dimmed">
                {release.date}
              </Text>
              {release.mandatory && (
                <Badge size="sm" color="red" variant="light">
                  Required
                </Badge>
              )}
            </Group>

            <Stack gap={6}>
              {release.changes.map((change) => (
                <Group key={change.text} gap="sm" wrap="nowrap" align="flex-start">
                  <Badge
                    size="xs"
                    variant="light"
                    color={KIND_COLOUR[change.kind] ?? 'gray'}
                    style={{ flexShrink: 0, marginTop: 2 }}
                  >
                    {KIND_LABEL[change.kind] ?? change.kind}
                  </Badge>
                  <Text size="sm">{change.text}</Text>
                </Group>
              ))}
            </Stack>

            {index < pending.length - 1 && <Divider />}
          </Stack>
        ))}
      </Stack>
    </ScrollArea>
  );

  return (
    <>
      {!mandatory && (
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconSparkles size={14} />}
          onClick={openModal}
        >
          Update available
        </Button>
      )}

      {mandatory && (
        <Group justify="center" gap="sm" wrap="nowrap">
          <IconAlertTriangle
            size={16}
            style={{ color: 'var(--mantine-color-red-6)', flexShrink: 0 }}
          />
          <Text size="sm" fw={600} style={{ whiteSpace: 'nowrap' }}>
            Required update
          </Text>
          {/* Dropped on narrow screens: the countdown and the buttons are what
              matter, and keeping this would wrap the row out of the footer. */}
          <Text size="sm" visibleFrom="sm" style={{ whiteSpace: 'nowrap' }}>
            {remaining !== null
              ? `Reloading automatically in ${formatRemaining(remaining)}`
              : 'Reloading shortly'}
          </Text>
          <Text size="sm" hiddenFrom="sm" style={{ whiteSpace: 'nowrap' }}>
            {remaining !== null ? formatRemaining(remaining) : ''}
          </Text>
          <Button size="compact-sm" variant="subtle" onClick={openModal}>
            What&apos;s new
          </Button>
          <Button
            size="compact-sm"
            color="red"
            leftSection={<IconRefresh size={14} />}
            onClick={reload}
          >
            Reload now
          </Button>
        </Group>
      )}

      <Modal
        opened={modalOpen}
        onClose={closeModal}
        title={pending.length === 1 ? "What's new" : `What's new in ${pending.length} updates`}
        size="lg"
      >
        <Stack gap="md">
          {changeList}
          <Divider />
          <Group justify="space-between" wrap="wrap">
            <Text size="xs" c="dimmed">
              {mandatory
                ? 'This update is required, so the page will reload on its own shortly.'
                : 'You can keep working. The update applies next time the page loads.'}
            </Text>
            <Button leftSection={<IconRefresh size={14} />} onClick={reload}>
              Reload now
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default AppUpdateNotice;
